import { useState } from 'react';
import { useHeadlineStore } from '../stores/headlineStore';
import type { Headline, ReadHeadline } from '../types';
import ClassifierCorrectionModal from './ClassifierCorrectionModal';

const SOURCE_ABBREV: Record<string, string> = {
  hn: 'HN',
  techmeme: 'TM',
  'bbc-world': 'BBC',
  tagesschau: 'TS',
  nytimes: 'NYT',
  guardian: 'GU',
  'reddit-worldnews': 'RWN',
};

interface Props {
  headline: Headline | ReadHeadline;
  showSource: boolean;
  isRead: boolean;
  pinned?: boolean;
}

export default function HeadlineItem({
  headline,
  showSource,
  isRead,
  pinned,
}: Props) {
  const clickHeadline = useHeadlineStore((s) => s.clickHeadline);
  const toggleSummary = useHeadlineStore((s) => s.toggleSummary);
  const summaryData = useHeadlineStore((s) => s.summaries[headline.id]);
  const interestMode = useHeadlineStore((s) => s.interestMode);

  const [correctionOpen, setCorrectionOpen] = useState(false);

  const handleClick = () => {
    if (isRead) return;
    clickHeadline(headline.id);
  };

  const match = headline.interest_match;
  const liClass =
    match === 'no-interest' || match === 'both'
      ? 'interest-no-match'
      : undefined;

  return (
    <li className={liClass}>
      {pinned && <span className="pin-icon">{'\u{1F4CC}'}</span>}
      {match === 'interest' && <span className="interest-match">[+] </span>}
      {match === 'no-interest' && (
        <span className="interest-no-match-marker">[-] </span>
      )}
      {match === 'both' && (
        <span className="interest-both-marker">[&plusmn;] </span>
      )}
      <a
        className="headline"
        href={headline.url}
        target="_blank"
        rel="noopener"
        onClick={handleClick}
      >
        {headline.title}
      </a>
      {showSource && (
        <span className="source-label">
          [{SOURCE_ABBREV[headline.source_slug] || headline.source_slug}]
        </span>
      )}
      <a
        className="src-link"
        href={headline.source_url}
        target="_blank"
        rel="noopener noreferrer"
      >
        (src)
      </a>
      <button
        className="summary-btn"
        type="button"
        onClick={() => toggleSummary(headline.id)}
      >
        (summary)
      </button>
      {interestMode === 'classifiers' && (
        <button
          className="adjust-btn"
          type="button"
          onClick={() => setCorrectionOpen(true)}
        >
          (adjust)
        </button>
      )}
      <div>
        {summaryData === 'loading' && (
          <div className="summary-loading">Loading summary…</div>
        )}
        {Array.isArray(summaryData) && (
          <ul className="summary-bullets">
            {summaryData.map((s) => (
              <li key={s}>{s}</li>
            ))}
          </ul>
        )}
      </div>
      {correctionOpen && (
        <ClassifierCorrectionModal
          headlineId={headline.id}
          headlineTitle={headline.title}
          onClose={() => setCorrectionOpen(false)}
        />
      )}
    </li>
  );
}
